import type { ReactNode } from 'react';
import { Switch as HeadlessSwitch } from '@headlessui/react';
import { clsx } from 'clsx';

export interface SimpleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
  /** Label beside switch (e.g. Baltic header); default stacks label and control with space-between */
  inline?: boolean;
}

/**
 * SimpleSwitch - Switch component without Formik dependency
 */
export function SimpleSwitch({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  className,
  inline = false,
}: SimpleSwitchProps) {
  return (
    <HeadlessSwitch.Group>
      <div className={clsx('flex items-center', inline ? 'gap-2' : 'justify-between', className)}>
        {label && (
          <div className="flex-1">
            <HeadlessSwitch.Label
              className={clsx(
                'cursor-pointer text-sm font-medium',
                disabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'
              )}
            >
              {label}
            </HeadlessSwitch.Label>
            {description && (
              <HeadlessSwitch.Description className="text-sm text-gray-500 dark:text-gray-400">
                {description}
              </HeadlessSwitch.Description>
            )}
          </div>
        )}
        <HeadlessSwitch
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className={clsx(
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            checked ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-gray-700',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <span
            className={clsx(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200',
              checked ? 'translate-x-6' : 'translate-x-1'
            )}
          />
        </HeadlessSwitch>
      </div>
    </HeadlessSwitch.Group>
  );
}
